import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import type { ReactElement } from 'react';

import { ROOT_ELEMENT_SELECTOR } from '../../consts';
import { useDialogContext } from '../../contexts/DialogContext';
import type { DialogItemProps, DialogType } from '../../contexts/DialogContext';

import AlertDialog from './AlertDialog';
import ConfirmDialog from './ConfirmDialog';
import ConfirmWithInputDialog from './ConfirmWithInputDialog';

//  Map a DialogType to the dialog component to use.
const dialogMap: Record<DialogType, (props: DialogItemProps<any>) => ReactElement> = {
  alert: AlertDialog,
  confirm: ConfirmDialog,
  confirmWithInput: ConfirmWithInputDialog,
};

/**
 * DialogManager will render any dialogs in the DialogContext.
 * Instead of rendering them as direct descendants in the DOM,
 * DialogManager creates a Portal and appends the root element (#dashboard-main).
 */
const DialogManager = () => {
  const { state } = useDialogContext();
  const rootElement = useRef<HTMLElement | null>(null);

  useEffect(() => {
    rootElement.current = document.querySelector(ROOT_ELEMENT_SELECTOR);
  }, []);

  return state.map(({ type, ...dialogProps }) => {
    //  This is to appease TS/avoid a React error if there is no root element.
    if (!rootElement.current) {
      return null;
    }

    const Component = dialogMap[type];
    return createPortal(
      (
        <Component
          {...dialogProps}
        />
      ),
      rootElement.current,
    );
  }) as unknown as ReactElement; // This helps fix the types in the consuming component
};

export default DialogManager;
